import React from 'react';
import { useVerifyEmailOtp, useResendEmailOtp, useSignupVerifyEmailOtpState } from './hooks';
import Spinner from '@atlaskit/spinner';
import {
  VerifyEmailOtp,
  VerifyEmailOtpErrorCode,
} from '../../../../components/VerifyEmailOtp/VerifyEmailOtp';
import { VERIFY_EMAIL_OTP_PAGE_ID } from './constants';
import { VerifyEmailOtpError } from '../../../../types';
import { fetchPageState } from '../../../../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../../../../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../../../../utilities/feature-flags/feature-gate';

const verifyEmailOtpErrorCodeMapping: Record<VerifyEmailOtpError, VerifyEmailOtpErrorCode> = {
  invalid_otp: 'invalid_otp',
  invalid_otp_length: 'invalid_otp_length',
  invalid_token: 'invalid_token',
  expired_token: 'expired_token',
  rate_limit_exceeded: 'rate_limited',
  internal_server_error: 'unexpected_error',
  unexpected_error: 'unexpected_error',
  'Too many requests. Please inspect Retry-After header.': 'rate_limited',
};

export async function signupVerifyEmailOtpLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/signup/verify-email/otp/state', request);
}

export const VerifyEmailOtpPage = () => {
  const { onVerifyEmailOtp, onInvalidEmailOtpLength, isLoading, error } = useVerifyEmailOtp();
  const { onResendEmailOtp } = useResendEmailOtp();
  const {
    state,
    isLoading: isStateLoading,
    error: pageStateError,
  } = useSignupVerifyEmailOtpState();

  if (isStateLoading) {
    return <Spinner label="Signup verify email OTP page loading" />;
  } else if (!state || pageStateError) {
    throw new Error(`Page state loading unsuccessful. \nPage state error: ${pageStateError}`);
  }

  const { email } = state;
  const verifyEmailOtpErrorCode = !error?.errorCode
    ? undefined
    : verifyEmailOtpErrorCodeMapping[error?.errorCode] || 'unexpected_error';

  return (
    <VerifyEmailOtp
      pageId={VERIFY_EMAIL_OTP_PAGE_ID}
      flow="sign-up"
      email={email}
      isLoading={isLoading}
      errorCode={verifyEmailOtpErrorCode}
      onVerifyEmailOtp={onVerifyEmailOtp}
      onResendEmailOtp={onResendEmailOtp}
      onInvalidEmailOtpLength={onInvalidEmailOtpLength}
    />
  );
};

export default VerifyEmailOtpPage;
